import { moveRequirement, TASK_STATUS_LABEL, taskAccess, type TaskStatus } from '@supply/contracts'
import * as React from 'react'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { moveTask, updateTask } from '@/data/api'
import type { Task } from '@/data/types'
import { invalidate } from '@/data/use-load'
import { useSession } from '@/lib/session'
import { useWrite } from '@/lib/use-write'

import { DeliveredDialog } from './DeliveredDialog'

/** "SUP-12 iniciada", "SUP-12 reaberta"… — o toast diz o passo, não só o status. */
export function moveVerb(from: TaskStatus, to: TaskStatus): string {
  if (to === 'realizada') return 'concluída'
  if (to === 'cancelada') return 'cancelada'
  if (from === 'realizada' || from === 'cancelada') return `reaberta em ${TASK_STATUS_LABEL[to]}`
  if (to === 'em_processo') return 'iniciada'
  return `voltou para ${TASK_STATUS_LABEL[to]}`
}

interface Pending {
  task: Task
  to: TaskStatus
  position: number | undefined
  kind: 'quantidade' | 'motivo'
}

/**
 * Mudança de status com o que ela pede antes (molde do Hub SISTEMAS): fechar
 * com mais de uma unidade pergunta quanto foi entregue; cancelar pede o
 * motivo. Usado pelo quadro (arrastar e menu "Mover para") e pelo detalhe
 * (botões Iniciar / Concluir / Cancelar / Reabrir).
 *
 * `onStart` deixa o quadro mostrar o card no lugar novo já no clique (e
 * enquanto o diálogo está aberto); `onSettled(false)` devolve o card.
 */
export function useStatusMove(options?: {
  onStart?: (task: Task, to: TaskStatus, position: number | undefined) => void
  onSettled?: (task: Task, ok: boolean) => void
}) {
  const { me } = useSession()
  const { run, pending } = useWrite()
  const [ask, setAsk] = React.useState<Pending | null>(null)
  const latest = React.useRef(options)
  latest.current = options

  const persist = React.useCallback(
    async (task: Task, to: TaskStatus, position: number | undefined, extra?: { qtd?: number; motivo?: string }) => {
      latest.current?.onStart?.(task, to, position)
      const label = { success: `${task.key} ${moveVerb(task.status, to)}`, error: 'Não foi possível mover a tarefa' }
      let ok: boolean
      if (to === 'cancelada') {
        // Motivo e status na mesma escrita. O executor de outra área não grava
        // `cancel_reason` (trigger task_guard_fields): o motivo vai na observação.
        const motivo = extra?.motivo?.trim() || null
        const r = await run(
          updateTask(task.id, {
            status: 'cancelada',
            ...(position !== undefined ? { position } : {}),
            ...(taskAccess(me, task) === 'total' ? { cancel_reason: motivo } : { status_note: motivo }),
          }),
          label,
        )
        ok = r.ok
      } else {
        const r = await run(moveTask(task.id, to, position, extra?.qtd), label)
        ok = r.ok
      }
      latest.current?.onSettled?.(task, ok)
      if (ok) invalidate()
      return ok
    },
    [me, run],
  )

  const request = React.useCallback(
    (task: Task, to: TaskStatus, position?: number) => {
      if (to === task.status && position === undefined) return
      const kind = moveRequirement(task, to)
      if (!kind) return void persist(task, to, position)
      latest.current?.onStart?.(task, to, position)
      setAsk({ task, to, position, kind })
    },
    [persist],
  )

  const cancelAsk = () => {
    if (ask) latest.current?.onSettled?.(ask.task, false)
    setAsk(null)
  }

  const dialogs = (
    <>
      <DeliveredDialog
        task={ask?.kind === 'quantidade' ? ask.task : null}
        onCancel={cancelAsk}
        onConfirm={(qtd) => {
          if (!ask) return
          setAsk(null)
          void persist(ask.task, ask.to, ask.position, { qtd })
        }}
      />
      <CancelReasonDialog
        task={ask?.kind === 'motivo' ? ask.task : null}
        onCancel={cancelAsk}
        onConfirm={(motivo) => {
          if (!ask) return
          setAsk(null)
          void persist(ask.task, ask.to, ask.position, { motivo })
        }}
      />
    </>
  )

  return { request, dialogs, pending }
}

/** Cancelar pede o porquê: na RAS, "cancelada" sem motivo vira pergunta. */
function CancelReasonDialog({
  task,
  onConfirm,
  onCancel,
}: {
  task: Task | null
  onConfirm: (motivo: string) => void
  onCancel: () => void
}) {
  const [value, setValue] = React.useState('')
  React.useEffect(() => {
    if (task) setValue('')
  }, [task])
  const valid = value.trim().length >= 3

  return (
    <Dialog open={task !== null} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Cancelar {task?.key}?</DialogTitle>
          <DialogDescription>
            A tarefa fecha sem entrega e sai das semanas seguintes. Dá para reabrir depois; o motivo fica no histórico da RAS.
          </DialogDescription>
        </DialogHeader>
        <form
          id="cancel-reason-form"
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (valid) onConfirm(value.trim())
          }}
        >
          <Label htmlFor="cancel-reason">Motivo do cancelamento</Label>
          <Textarea
            id="cancel-reason"
            rows={3}
            value={value}
            autoFocus
            placeholder="Ex.: fornecedor desistiu, demanda absorvida por outra tarefa"
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && valid) {
                e.preventDefault()
                onConfirm(value.trim())
              }
            }}
          />
          <p className="text-xs text-muted-foreground">Mínimo de 3 caracteres.</p>
        </form>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            Voltar
          </Button>
          <Button type="submit" form="cancel-reason-form" variant="destructive" disabled={!valid}>
            Cancelar tarefa
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
